import React, { createContext, useContext, useMemo } from 'react';

import type { MapManager } from '@/core/modules/MapManager';
import type { IncidentFeedItem, IncidentItem } from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types';

/**
 * Map, incidents and selection state as consumed by the incidents feed panel, independent of the page that hosts it.
 * `useIncidentsFeedSource` throws when no `IncidentsFeedSourceProvider` is mounted.
 *
 * Filters are intentionally *not* here: they live in `IncidentFiltersSource` as the single source
 * of truth. Consumers that need them read `useIncidentFiltersSource().filters` directly.
 */
export interface IncidentsFeedSource {
  map: MapManager | null;
  incidents: IncidentItem[];
  activeIncidents: IncidentItem[] | null;
  setActiveIncidents(items: IncidentItem[] | null): void;
  focusMaxZoom?: number;
  showIncidentDetails(item: IncidentFeedItem): void;
}

interface IncidentsFeedSourceProviderProps {
  map: MapManager | null;
  incidents: IncidentItem[];
  activeIncidents: IncidentItem[] | null;
  onActiveIncidentsChange: IncidentsFeedSource['setActiveIncidents'];
  onShowIncidentDetails: IncidentsFeedSource['showIncidentDetails'];
  focusMaxZoom?: number;
}

export const IncidentsFeedSourceContext = createContext<IncidentsFeedSource | undefined>(undefined);

/**
 * Takes typed props and builds the context value internally, so hosts don't repeat the `useMemo`
 * plumbing. Must not read page-specific contexts; everything arrives as props. Filters are not part
 * of this source — consumers read `useIncidentFiltersSource().filters` directly.
 */
export const IncidentsFeedSourceProvider: React.FC<React.PropsWithChildren<IncidentsFeedSourceProviderProps>> = ({
  map,
  incidents,
  activeIncidents,
  onActiveIncidentsChange,
  onShowIncidentDetails,
  focusMaxZoom,
  children,
}) => {
  const value = useMemo<IncidentsFeedSource>(
    () => ({
      map,
      incidents,
      activeIncidents,
      setActiveIncidents: onActiveIncidentsChange,
      focusMaxZoom,
      showIncidentDetails: onShowIncidentDetails,
    }),
    [map, incidents, activeIncidents, onActiveIncidentsChange, focusMaxZoom, onShowIncidentDetails],
  );

  return React.createElement(IncidentsFeedSourceContext.Provider, { value }, children);
};

export const useIncidentsFeedSource = (): IncidentsFeedSource => {
  const context = useContext(IncidentsFeedSourceContext);

  if (!context) {
    throw new Error('useIncidentsFeedSource must be used within an IncidentsFeedSourceProvider');
  }

  return context;
};
